import React, {useState, useMemo, useCallback, useRef, forwardRef, useImperativeHandle, useEffect} from 'react';
import {Make, Model} from '../../types';
import {modelSearchService} from '../../services';
import {RequiredIndicator, FieldError} from '../ui';
import {AsyncSearchSelect} from '../forms';

interface CarDataSectionProps {
    chassisNumber: string;
    makeId: string;
    modelId: string;
    manualMakeModel?: string;
    initialMakeModel?: string; // Pre-loaded make/model display value from car data
    year: number | null;
    category: string;
    length: number;
    width: number;
    height: number;
    weight: number;
    cbm: number;
    onChassisNumberChange: (value: string) => void;
    onMakeChange: (makeId: string, displayLabel?: string) => void;
    onModelChange: (modelId: string, autoFillData?: {
        category?: string;
        length: number;
        width: number;
        height: number;
        weight: number;
        cbm: number
    }) => void;
    onManualMakeModelChange: (value: string) => void;
    onYearChange: (value: number | null) => void;
    onCategoryChange: (value: string) => void;
    onDimensionsChange: (field: 'length' | 'width' | 'height' | 'weight' | 'cbm', value: number) => void;
    disabled?: boolean;
    fieldErrors?: Record<string, string>;
}

export interface CarDataSectionRef {
    focusChassisInput: () => void;
}

export const CarDataSection = forwardRef<CarDataSectionRef, CarDataSectionProps>(({
                                                                  chassisNumber,
                                                                  makeId,
                                                                  modelId,
                                                                  manualMakeModel = '',
                                                                  initialMakeModel,
                                                                  year,
                                                                  category,
                                                                  length,
                                                                  width,
                                                                  height,
                                                                  weight,
                                                                  cbm,
                                                                  onChassisNumberChange,
                                                                  onMakeChange,
                                                                  onModelChange,
                                                                  onManualMakeModelChange,
                                                                  onYearChange,
                                                                  onCategoryChange,
                                                                  onDimensionsChange,
                                                                  disabled = false,
                                                                  fieldErrors = {}
                                                              }, ref) => {
    const [isManualEntry, setIsManualEntry] = useState(!!manualMakeModel);
    const chassisInputRef = useRef<HTMLInputElement>(null);

    // Update isManualEntry when manualMakeModel is loaded (e.g., editing existing car)
    useEffect(() => {
        if (manualMakeModel) {
            setIsManualEntry(true);
        }
    }, [manualMakeModel]);

    // Expose focusChassisInput method to parent via ref
    useImperativeHandle(ref, () => ({
        focusChassisInput: () => {
            chassisInputRef.current?.focus();
            chassisInputRef.current?.select();
        }
    }));

    // Load models from backend
    const loadModels = useCallback(async (query: string): Promise<Array<{ model: Model; make: Make }>> => {
        try {
            const results = await modelSearchService.searchWithMake(query, 50);
            return results.map(result => ({
                model: {
                    id: result.id,
                    code: result.code,
                    name: result.name,
                    type: result.type,
                    weight: result.weight,
                    length: result.length,
                    width: result.width,
                    height: result.height,
                    cbm: result.cbm,
                    make_id: result.make_id,
                } as Model,
                make: {
                    id: result.make.id,
                    code: result.make.code,
                    name: result.make.name,
                    created_at: '',
                    creator_id: '',
                } as Make
            }));
        } catch (error: any) {
            if (error.name !== 'AbortError') {
                console.error('Search error:', error);
            }
            return [];
        }
    }, []);

    // Initial display value for the model dropdown
    const modelInitialValue = useMemo(() => {
        if (!modelId) return '';
        return initialMakeModel || '';
    }, [modelId, initialMakeModel]);

    const handleSelectModel = (option: { model: Model; make: Make }) => {
        if (!option.make) return;
        const displayLabel = `${option.make.name} - ${option.model.name}`;
        onMakeChange(option.make.id, displayLabel);
        onModelChange(option.model.id, {
            category: option.model.type,
            length: option.model.length,
            width: option.model.width,
            height: option.model.height,
            weight: option.model.weight,
            cbm: option.model.cbm
        });
    };

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1.5 pb-1 border-b border-gray-200 dark:border-gray-700 flex items-center">
                <svg className="w-3.5 h-3.5 mr-1.5 text-purple-600" fill="none" stroke="currentColor"
                     viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"/>
                </svg>
                Car Data
            </h3>

            <div className="space-y-1.5">
                {/* Row 1: Chassis Number + Make & Model */}
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            Chassis Number <RequiredIndicator/>
                        </label>
                        <div className="relative">
                            <input
                                ref={chassisInputRef}
                                type="text"
                                value={chassisNumber}
                                onChange={(e) => e.target.value.length <= 50 && onChassisNumberChange(e.target.value)}
                                disabled={disabled}
                                placeholder="Enter chassis number..."
                                className={`w-full px-2 py-1 pr-10 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 ${
                                    fieldErrors.chassisNumber ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                                }`}
                            />
                            <span className={`absolute right-2 top-1.5 text-xs font-medium ${
                                chassisNumber.length === 17 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
                            }`}>
                                {chassisNumber.length}
                            </span>
                        </div>
                        <FieldError error={fieldErrors.chassisNumber} className="text-xs"/>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-0.5">
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                Make & Model <RequiredIndicator/>
                            </label>
                            <button
                                type="button"
                                onClick={() => setIsManualEntry(!isManualEntry)}
                                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            >
                                {isManualEntry ? 'Search Database' : 'Manual Entry'}
                            </button>
                        </div>
                        {isManualEntry ? (
                            <input
                                type="text"
                                value={manualMakeModel}
                                onChange={(e) => onManualMakeModelChange(e.target.value)}
                                disabled={disabled}
                                placeholder="e.g., Generator, Crane basket..."
                                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
                            />
                        ) : (
                            <AsyncSearchSelect<{ model: Model; make: Make }>
                                loadOptions={loadModels}
                                getValue={(option) => option.model.id}
                                getLabel={(option) => `${option.make.name} - ${option.model.name}`}
                                getSecondaryLabel={(option) => `${option.model.type} | ${option.model.weight}kg | ${option.model.length}×${option.model.width}×${option.model.height}m`}
                                onSelect={handleSelectModel}
                                value={modelId}
                                initialDisplayValue={modelInitialValue}
                                placeholder="Search by make or model..."
                                disabled={disabled}
                                noOptionsMessage="No models found"
                            />
                        )}
                    </div>
                </div>

                {/* Row 2: Category + Year */}
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            Category
                        </label>
                        <select
                            value={category}
                            onChange={(e) => onCategoryChange(e.target.value)}
                            disabled={disabled}
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
                        >
                            <option value="">Select category</option>
                            <option value="B">B</option>
                            <option value="P">P</option>
                            <option value="G">G</option>
                            <option value="L">L</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            Year
                        </label>
                        <input
                            type="number"
                            value={year ?? ''}
                            onChange={(e) => onYearChange(e.target.value === '' ? null : Number(e.target.value))}
                            onWheel={(e) => e.currentTarget.blur()}
                            disabled={disabled}
                            placeholder="Year"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                    </div>
                </div>

                {/* Row 3: Dimensions */}
                <div className="grid grid-cols-5 gap-1.5">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            Length (m)
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={length || ''}
                            onChange={(e) => onDimensionsChange('length', Number(e.target.value))}
                            onWheel={(e) => e.currentTarget.blur()}
                            disabled={disabled}
                            className="w-full px-1.5 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            Width (m)
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={width || ''}
                            onChange={(e) => onDimensionsChange('width', Number(e.target.value))}
                            onWheel={(e) => e.currentTarget.blur()}
                            disabled={disabled}
                            className="w-full px-1.5 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            Height (m)
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={height || ''}
                            onChange={(e) => onDimensionsChange('height', Number(e.target.value))}
                            onWheel={(e) => e.currentTarget.blur()}
                            disabled={disabled}
                            className="w-full px-1.5 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            Weight (kg)
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={weight || ''}
                            onChange={(e) => onDimensionsChange('weight', Number(e.target.value))}
                            onWheel={(e) => e.currentTarget.blur()}
                            disabled={disabled}
                            className="w-full px-1.5 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            CBM <span className="text-[10px] text-gray-500 dark:text-gray-400 font-normal">(auto)</span>
                        </label>
                        <input
                            type="number"
                            step="0.001"
                            value={cbm || ''}
                            readOnly
                            disabled
                            className="w-full px-1.5 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-800 dark:text-white cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
});
